import { api } from "./api";
import { API_ENDPOINTS } from "../../utils/api-service";
import { CsvExportResponse, isCsvDownloadResponse, readCsvResponse } from "../../utils/functions/readCsvResponse";
import {
  Attendance,
  AttendanceHistoryPage,
  AttendanceListFilters,
  AttendancePage,
  EventAttendanceConfig,
  AttendanceFilters,
  SaveEventAttendanceConfigPayload,
} from "../../models/attendance";

const attendanceTag = (id: number) => ({ type: "attendances" as const, id });
const attendanceListTag = { type: "attendances" as const, id: "LIST" };
const attendanceEventOptionsTag = { type: "attendanceConfig" as const, id: "EVENT_OPTIONS" };
const eventListTagForOptions = { type: "events" as const, id: "LIST" };
const attendanceConfigTag = (eventId: number) => ({
  type: "attendanceConfig" as const,
  id: eventId,
});

const unwrapApiData = (response: unknown): unknown => {
  if (typeof response !== "object" || response === null) {
    return response;
  }
  const record = response as Record<string, unknown>;
  return record.success === true && record.data !== undefined ? record.data : response;
};

const toInt = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toStr = (value: unknown): string => (typeof value === "string" ? value : "");

const toNullableStr = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

const toBool = (value: unknown): boolean => value === true;

const mapAttendance = (raw: unknown): Attendance => {
  const row = (raw ?? {}) as Record<string, unknown>;
  return {
    id: toInt(row.id, 0),
    tenantId: toStr(row.tenant_id),
    tenantName: toStr(row.tenant_name),
    eventId: toInt(row.event_id, 0),
    eventName: toStr(row.event_name),
    eventStartDate: toStr(row.event_start_date),
    personUserId:
      row.person_user_id === null || row.person_user_id === undefined
        ? null
        : toInt(row.person_user_id, 0),
    personName: toStr(row.person_name),
    personEmail: toNullableStr(row.person_email),
    personAvatarUrl: toNullableStr(row.person_avatar_url),
    personDocument: toNullableStr(row.person_document),
    isCompanion: toBool(row.is_companion),
    hostName: toNullableStr(row.host_name),
    hostAvatarUrl: toNullableStr(row.host_avatar_url),
    markedAt: toStr(row.marked_at),
    isPresent: toBool(row.is_present),
    source: row.source === "BO" ? "BO" : "APP",
    updatedAt: toStr(row.updated_at),
    windowStartOffsetMin: toInt(row.window_start_offset_min, 0),
    windowEndHours: toInt(row.window_end_hours, 24),
  };
};

const mapEventAttendanceConfig = (raw: unknown): EventAttendanceConfig | null => {
  if (raw === null || raw === undefined) {
    return null;
  }
  const row = raw as Record<string, unknown>;
  return {
    id: toInt(row.id, 0),
    eventId: toInt(row.event_id, 0),
    tenantId: toStr(row.tenant_id),
    enabled: toBool(row.enabled),
    method: "MANUAL_APP",
    attendanceHostUserId:
      row.attendance_host_user_id === null ? null : toInt(row.attendance_host_user_id, 0),
    maxAttendees: toInt(row.max_attendees, 20),
    windowStartOffsetMin: toInt(row.window_start_offset_min, 0),
    windowEndHours: toInt(row.window_end_hours, 24),
    createdAt: toStr(row.created_at),
    updatedAt: toStr(row.updated_at),
    deletedAt: toNullableStr(row.deleted_at),
  };
};

const attendanceFilterParams = (filters: AttendanceFilters) => ({
  ...(filters.tenantId ? { tenant_id: filters.tenantId } : {}),
  ...(filters.eventId ? { event_id: filters.eventId } : {}),
  ...(filters.eventName ? { event_name: filters.eventName } : {}),
  ...(filters.dateFrom ? { date_from: filters.dateFrom } : {}),
  ...(filters.dateTo ? { date_to: filters.dateTo } : {}),
  ...(filters.search ? { search: filters.search } : {}),
  ...(filters.isPresent !== undefined ? { is_present: String(filters.isPresent) } : {}),
  ...(filters.sortBy ? { sort_by: filters.sortBy } : {}),
  ...(filters.sortDir ? { sort_dir: filters.sortDir } : {}),
});

const attendanceQueryParams = (filters: AttendanceListFilters) => ({
  ...attendanceFilterParams(filters),
  page: filters.page,
  per_page: filters.perPage,
});

export const attendancesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAttendanceEventOptions: build.query<Array<{ id: number; name: string }>, { search: string; tenantId: string | null; purpose?: "registration" | "history" }>({
      query: ({ search, purpose = "registration" }) => ({ url: "/admin/attendance/options/events", params: { search, purpose } }),
      transformResponse: (response: { success: boolean; data: Array<{ id: number; name: string }> }) => response.data,
      providesTags: [attendanceEventOptionsTag, eventListTagForOptions],
    }),
    getAttendanceHostOptions: build.query<Array<{ id: number; name: string }>, number>({
      query: (eventId) => ({ url: `/admin/attendance/options/hosts/${eventId}` }),
      transformResponse: (response: { success: boolean; data: Array<{ id: number; name: string }> }) => response.data,
      providesTags: (_result, _error, eventId) => [{ type: "events", id: `${eventId}-users` }],
    }),
    getAttendances: build.query<AttendancePage, AttendanceListFilters>({
      query: (filters) => ({
        url: API_ENDPOINTS.ATTENDANCES.LIST,
        params: attendanceQueryParams(filters),
      }),
      transformResponse: (response: unknown, _meta, filters): AttendancePage => {
        const data = (unwrapApiData(response) ?? {}) as Record<string, unknown>;
        const rows = Array.isArray(data.data) ? data.data : [];
        const pagination = (data.pagination ?? {}) as Record<string, unknown>;
        return {
          items: rows.map(mapAttendance),
          total: toInt(pagination.total, rows.length),
          page: toInt(pagination.page, filters.page),
          perPage: toInt(pagination.per_page, filters.perPage),
          totalPages: toInt(pagination.total_pages, 0),
        };
      },
      providesTags: (result) => [
        ...(result?.items ?? []).map((item) => attendanceTag(item.id)),
        attendanceListTag,
      ],
    }),

    getUserAttendanceHistory: build.query<
      AttendanceHistoryPage,
      { userId: number; tenantId: string | null; page: number; perPage: number }
    >({
      query: ({ userId, tenantId, page, perPage }) => ({
        url: API_ENDPOINTS.ATTENDANCES.USER_HISTORY(userId),
        params: {
          ...(tenantId ? { tenant_id: tenantId } : {}),
          page,
          per_page: perPage,
        },
      }),
      transformResponse: (response: unknown, _meta, args): AttendanceHistoryPage => {
        const data = (unwrapApiData(response) ?? {}) as Record<string, unknown>;
        const person = (data.user ?? {}) as Record<string, unknown>;
        const rows = Array.isArray(data.data) ? data.data : [];
        const pagination = (data.pagination ?? {}) as Record<string, unknown>;
        return {
          presentCount: typeof data.present_count === "number" ? data.present_count : null,
          absentCount: typeof data.absent_count === "number" ? data.absent_count : null,
          person: {
            id: toInt(person.id, args.userId),
            name: toStr(person.name),
            email: toStr(person.email),
            avatarUrl: toNullableStr(person.avatar_url),
          },
          items: rows.map((raw) => {
            const row = (raw ?? {}) as Record<string, unknown>;
            return {
              id: toInt(row.id, 0),
              eventId: toInt(row.event_id, 0),
              eventName: toStr(row.event_name),
              eventDate: toStr(row.event_date),
              isPresent: toBool(row.is_present),
              hostName: toNullableStr(row.host_name),
              markedAt: toStr(row.marked_at),
              hostAvatarUrl: toNullableStr(row.host_avatar_url),
              eventType: toNullableStr(row.event_type),
              eventSubtype: toNullableStr(row.event_subtype),
            };
          }),
          total: toInt(pagination.total, rows.length),
          page: toInt(pagination.page, args.page),
          perPage: toInt(pagination.per_page, args.perPage),
          totalPages: toInt(pagination.total_pages, 0),
        };
      },
      providesTags: (result) => [
        ...(result?.items ?? []).map((item) => attendanceTag(item.id)),
        attendanceListTag,
      ],
    }),

    exportAttendances: build.query<CsvExportResponse, AttendanceFilters>({
      query: (filters) => ({
        url: API_ENDPOINTS.ATTENDANCES.EXPORT,
        method: "GET",
        params: attendanceFilterParams(filters),
        responseHandler: readCsvResponse,
        validateStatus: (response) => isCsvDownloadResponse(response) || response.status === 202 || response.status === 204,
      }),
      keepUnusedDataFor: 0,
    }),

    getEventAttendanceConfig: build.query<EventAttendanceConfig | null, number>({
      query: (eventId) => ({ url: API_ENDPOINTS.ATTENDANCES.EVENT_CONFIG(eventId) }),
      transformResponse: (response: unknown) => mapEventAttendanceConfig(unwrapApiData(response)),
      providesTags: (_result, _error, eventId) => [attendanceConfigTag(eventId)],
    }),

    saveEventAttendanceConfig: build.mutation<
      EventAttendanceConfig | null,
      SaveEventAttendanceConfigPayload
    >({
      query: ({ eventId, ...settings }) => ({
        url: API_ENDPOINTS.ATTENDANCES.EVENT_CONFIG(eventId),
        method: "PUT",
        body: {
          enabled: settings.enabled,
          method: settings.method,
          attendance_host_user_id: settings.attendanceHostUserId,
          max_attendees: settings.maxAttendees,
          window_start_offset_min: settings.windowStartOffsetMin,
          window_end_hours: settings.windowEndHours,
        },
      }),
      transformResponse: (response: unknown) => mapEventAttendanceConfig(unwrapApiData(response)),
      invalidatesTags: (_result, _error, { eventId }) => [
        attendanceConfigTag(eventId),
        attendanceListTag,
        attendanceEventOptionsTag,
      ],
    }),
  }),
});

export const {
  useGetAttendancesQuery,
  useGetAttendanceEventOptionsQuery,
  useLazyGetAttendanceEventOptionsQuery,
  useGetAttendanceHostOptionsQuery,
  useGetUserAttendanceHistoryQuery,
  useLazyExportAttendancesQuery,
  useGetEventAttendanceConfigQuery,
  useSaveEventAttendanceConfigMutation,
} = attendancesApi;
